import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../singleton/axios';
import { authClient } from '../lib/auth-client';
import {
  CheckoutRequest,
  CheckoutResponse,
  CreditsCheckoutRequest,
  CreditsCheckoutResponse,
  MembershipStatus,
} from '../types/lemonSqueezy';
import { getMetaAttribution } from '../lib/metaPixel';

/**
 * Hook to get current user's membership status
 * Follows the same pattern as useEarningsWatchlist
 *
 * @param options - Optional configuration for caching behavior
 * @param options.noCache - If true, always fetches fresh data (staleTime: 0)
 */
export const useMembershipStatus = (options?: {
  noCache?: boolean;
}) => {
  return useQuery({
    queryKey: ['membershipStatus'],
    queryFn: async () => {
      const response = await api.get<MembershipStatus>('/membership');
      return response.data;
    },
    staleTime: options?.noCache ? 0 : 1000 * 60 * 5, // No cache if noCache=true, otherwise 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
};

/**
 * A failed checkout. `updatePaymentMethodUrl` is set when the user's existing
 * subscription has a failed payment: they must fix that card, not buy again.
 */
export type CheckoutError = Error & { updatePaymentMethodUrl?: string };

/**
 * Hook to create checkout session
 * Returns mutation function for creating Lemon Squeezy checkout
 */
export const useCreateCheckout = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CheckoutRequest) => {
      try {
        const response = await api.post<CheckoutResponse>('/membership', {
          ...request,
          meta: getMetaAttribution(),
        });
        return response.data;
      } catch (error) {
        // Extract the actual API error message instead of generic axios error
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as {
            response?: { data?: { error?: string; message?: string; updatePaymentMethodUrl?: string } };
          };
          const apiError = axiosError.response?.data?.error ?? axiosError.response?.data?.message;
          if (apiError) {
            const checkoutError: CheckoutError = Object.assign(new Error(apiError), {
              updatePaymentMethodUrl: axiosError.response?.data?.updatePaymentMethodUrl,
            });
            throw checkoutError;
          }
        }
        throw error;
      }
    },
    onSuccess: () => {
      // Invalidate membership status to refresh after successful checkout creation
      queryClient.invalidateQueries({ queryKey: ['membershipStatus'] });
    },
  });
};

/**
 * Hook to switch a web subscription between Basic and Pro (POST
 * /membership/change-plan — immediate both ways; Lemon Squeezy prorates: an
 * upgrade charges the difference now, a downgrade credits unused Pro time).
 * WEB ONLY: native subscribers change plans through the store. On success this
 * device force-refreshes its session cookie so the new AI claims apply
 * instantly.
 */
export const useChangePlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: {
      target: 'pro' | 'basic';
      interval: 'monthly' | 'yearly';
    }) => {
      try {
        const response = await api.post<{
          status: 'upgraded' | 'downgraded';
          membership: { id: string; status: string; variantId: string; renewsAt: string | null };
        }>('/membership/change-plan', request);
        return response.data;
      } catch (error) {
        // Surface the API's error message instead of the generic axios error
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { data?: { error?: string } } };
          const apiError = axiosError.response?.data?.error;
          if (apiError) {
            throw new Error(apiError);
          }
        }
        throw error;
      }
    },
    onSuccess: async () => {
      // Bypass the 5-min cookie cache so this device's claims update now.
      await authClient.getSession({ query: { disableCookieCache: true } });
      queryClient.invalidateQueries({ queryKey: ['membershipStatus'] });
      queryClient.invalidateQueries({ queryKey: ['chat', 'quota'] });
      queryClient.invalidateQueries({ queryKey: ['stockAnalysisReportQuota'] });
      // The screener's allowance and saved-screen limit follow the plan too.
      queryClient.invalidateQueries({ queryKey: ['screener'] });
    },
  });
};

/**
 * Hook to create a Lemon Squeezy checkout for an AI credit top-up (Pro, WEB
 * ONLY — native buys credits as store consumables through RevenueCat and must
 * never reach this endpoint). Credits land via webhook after payment; the
 * quota surfaces re-read the balance live, so no session refresh is needed.
 */
export const useCreditsCheckout = () => {
  return useMutation({
    mutationFn: async (request: CreditsCheckoutRequest) => {
      try {
        const response = await api.post<CreditsCheckoutResponse>(
          '/membership/credits-checkout',
          request
        );
        return response.data;
      } catch (error) {
        // Surface the API's error message instead of the generic axios error
        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as { response?: { data?: { error?: string } } };
          const apiError = axiosError.response?.data?.error;
          if (apiError) {
            throw new Error(apiError);
          }
        }
        throw error;
      }
    },
  });
};

/**
 * Combined hook for complete checkout flow
 * Orchestrates checkout creation + Lemon.js overlay opening
 */
export const useCheckoutFlow = () => {
  const createCheckoutMutation = useCreateCheckout();
  const { data: membershipStatus, isLoading: isCheckingStatus } = useMembershipStatus();

  /**
   * Start checkout flow for a specific plan
   * Returns checkout URL that can be used with Lemon.js overlay
   */
  const startCheckout = async (variantId: string, customerName?: string): Promise<string> => {
    try {
      // Check if user already has active membership
      if (membershipStatus?.hasActiveMembership) {
        throw new Error('You already have an active membership');
      }

      // Create checkout session
      const checkoutData = await createCheckoutMutation.mutateAsync({
        variantId,
        name: customerName,
      });

      return checkoutData.data.checkoutUrl;
    } catch (error) {
      // Re-throw with context
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Failed to start checkout process');
    }
  };

  return {
    startCheckout,
    isCreatingCheckout: createCheckoutMutation.isPending,
    isCheckingMembership: isCheckingStatus,
    membershipStatus,
    error: createCheckoutMutation.error,
    reset: createCheckoutMutation.reset,
  };
};

/**
 * Hook to check if user needs to upgrade
 * Useful for conditional rendering of upgrade prompts
 */
export const useUpgradeStatus = () => {
  const { data: membershipStatus, isLoading } = useMembershipStatus();

  const needsUpgrade = !membershipStatus?.hasActiveMembership;
  const hasActiveMembership = membershipStatus?.hasActiveMembership ?? false;

  return {
    needsUpgrade,
    hasActiveMembership,
    isLoading,
    membershipStatus,
  };
};
